import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'

interface GenericModalProps {
    open: boolean
    onClose?: () => void
    primaryText: string
    secondaryText?: string
    primaryCTA?: string
    secondaryCTA?: string
    onPrimaryClick?: () => void
    onSecondaryClick?: () => void
    primaryButtonColor: string
    primaryButtonHover: string
    primaryButtonTextColor: string
    disableBackdropClick?: boolean
    handleBackdropClick: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void
}

const GenericModal: React.FC<GenericModalProps> = ({
    open,
    onClose,
    primaryText,
    secondaryText,
    primaryCTA,
    secondaryCTA,
    onPrimaryClick,
    onSecondaryClick,
    primaryButtonColor,
    primaryButtonHover,
    primaryButtonTextColor,
    disableBackdropClick = false,
    handleBackdropClick
}) => {
    return (
        <Dialog
            open={open}
            onClose={handleBackdropClick}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        mx: { xs: 2, sm: 3 },
                        my: { xs: 2, sm: 4 },
                        width: {
                            xs: 'calc(100vw - 32px)',
                            sm: 'auto',
                            md: '600px'
                        },
                        maxWidth: {
                            xs: 'calc(100vw - 32px)',
                            sm: '500px',
                            md: '600px'
                        }
                    }
                }
            }}
        >
            <DialogTitle sx={{ pb: 2, pt: { xs: 3, sm: 4 }, px: { xs: 3, sm: 4 } }}>
                <Typography
                    sx={{
                        fontWeight: 600,
                        color: 'textColors.primary',
                        textAlign: 'center',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }}
                >
                    {primaryText}
                </Typography>
            </DialogTitle>

            {secondaryText && (
                <DialogContent sx={{ pt: 0, pb: 3, px: { xs: 3, sm: 4 } }}>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'textColors.secondary',
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            lineHeight: 1.6,
                            textAlign: 'center'
                        }}
                    >
                        {secondaryText}
                    </Typography>
                </DialogContent>
            )}

            <DialogActions
                sx={{
                    px: { xs: 3, sm: 4 },
                    pb: { xs: 3, sm: 4 },
                    gap: { xs: 1.5, sm: 2 },
                    justifyContent: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                    '& .MuiButton-root': {
                        margin: 0
                    }
                }}
            >
                {primaryCTA && (
                    <Button
                        variant="customVariantPrimary"
                        onClick={onPrimaryClick}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            bgcolor: primaryButtonColor,
                            color: primaryButtonTextColor,
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1.25, sm: 1.5 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minWidth: { xs: '100%', sm: 140 },
                            width: '100%',
                            '&:hover': {
                                bgcolor: primaryButtonHover
                            }
                        }}
                    >
                        {primaryCTA}
                    </Button>
                )}

                {secondaryCTA && (
                    <Button
                        variant="outlined"
                        onClick={onSecondaryClick}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: 'grey.300',
                            color: 'textColors.secondary',
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1.25, sm: 1.5 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minWidth: { xs: '100%', sm: 140 },
                            width: '100%',
                            '&:hover': {
                                borderColor: 'grey.400',
                                backgroundColor: 'grey.50'
                            }
                        }}
                    >
                        {secondaryCTA}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export { GenericModal }
export type { GenericModalProps }
